import React from 'react';
import {
  HeaderActionMenu,
  HeaderActionMenuProps,
} from '@backstage/core-components';
import { Typography } from '@dewise-devex/backstage-plugin-mui-uikit-react';

export type PageHeaderProps = {
  children?: React.ReactNode;
  headerActionProps?: HeaderActionMenuProps;
  title: string;
  subtitle?: string;
  icon: React.ReactNode;
};

/**
 * `PageHeader` is a component that renders a header for a page.
 *
 * @param {PageHeaderProps} props - The props of the component.
 *
 * @returns {React.ReactElement} The rendered component.
 *
 * @category Layout
 *
 * @example
 * ```tsx
 * import { PageHeader } from '@dewise-devex/backstage-plugin-mui-uikit-react';
 *  <PageHeader
 *    subtitle="Documentation"
 *    title="Tech Docs"
 *    icon={<MaterialIcon.menu_book />}
 *    />
 * ```
 */
export const PageHeader = (props: PageHeaderProps): React.ReactElement => {
  return (
    <>
      <Container
        direction="column"
        justifyContent="flex-start"
        alignItems="baseline"
      >
        <Container direction="row">
          {props.icon}
          <Typography variant="titleSM" sx={{ ml: '8px', mt: '4px' }}>
            {props.title}
          </Typography>
        </Container>
        <Container direction="row" alignItems="center">
          {props.children ?? (
            <Typography variant="headlineLG">{props.subtitle}</Typography>
          )}
        </Container>
      </Container>
      {props.headerActionProps && (
        <HeaderActionMenu {...props.headerActionProps} />
      )}
    </>
  );
};

const Container = (props: {
  children?: React.ReactNode;
  direction?: 'row' | 'column';
  justifyContent?: string;
  alignItems?: string;
}) => {
  const { children, direction, justifyContent, alignItems } = props;
  const style: React.CSSProperties = {
    display: 'flex',
    flexDirection: direction,
    justifyContent,
    alignItems,
  };
  return <div style={style}>{children}</div>;
};
